import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AppIcon, Badge, Button, Dialog, ListTable, Notice, SelectField, TextField, type BadgeProps, type ListColumn } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { useCan } from "../auth.ts";
import { formatDate, formatMoney, formatNumber, t, type MessageKey } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { salesApi, type SalesOrder, type SalesState } from "./api.ts";
import { customersQuery, SALES, salesOrdersQuery, shipTasksQuery } from "./queries.ts";

const STATE: Record<SalesState, { key: MessageKey; tone: BadgeProps["tone"] }> = {
  DRAFT: { key: "sales.state.DRAFT", tone: "neutral" },
  CONFIRMED: { key: "sales.state.CONFIRMED", tone: "strong" },
  DONE: { key: "sales.state.DONE", tone: "success" },
  CANCELLED: { key: "sales.state.CANCELLED", tone: "neutral" },
};

export function SalesStateBadge({ state }: { state: SalesState }) {
  return <Badge tone={STATE[state].tone}>{t(STATE[state].key)}</Badge>;
}

export const orderTotal = (o: SalesOrder) => o.lines.reduce((a, l) => a + l.qty * l.unitPrice, 0);

/**
 * Заказы покупателей. Кладовщику сверху задачи «Відвантажити покупцю»
 * (ПС-02): отгрузка ждёт, когда изделие упаковано, срок дата заказа.
 */
export function SalesPage() {
  const navigate = useNavigate();
  const can = useCan();
  const list = useQuery(salesOrdersQuery);
  const tasks = useQuery({ ...shipTasksQuery, enabled: can("sales.ship") });
  const open = (id: string) => void navigate({ to: "/sales/orders/$id", params: { id } });

  const columns: ListColumn<SalesOrder>[] = [
    { key: "number", header: t("sales.number"), kind: "title", width: "9rem", render: (o) => <span className="w-num">{o.number}</span> },
    { key: "customer", header: t("sales.customer"), kind: "text", render: (o) => o.customerName },
    { key: "state", header: t("sales.state"), kind: "text", width: "9rem", render: (o) => <SalesStateBadge state={o.state} /> },
    { key: "ship", header: t("sales.shipDate"), kind: "number", width: "7rem", render: (o) => formatDate(o.shipDate) },
    {
      key: "left",
      header: t("sales.remaining"),
      kind: "number",
      width: "6rem",
      render: (o) => formatNumber(o.lines.reduce((a, l) => a + l.remaining, 0)),
    },
    { key: "sum", header: t("sales.total"), kind: "number", width: "9rem", render: (o) => formatMoney(orderTotal(o), "UAH") },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title page-title-icon">
          <AppIcon name="sales" size={32} />
          {t("sales.title")}
        </h1>
        {can("sales.order") ? <NewOrder onCreated={open} /> : null}
      </div>
      {tasks.data?.length ? (
        <Notice tone="info" title={t("sales.tasks", { count: tasks.data.length })} className="stock-notice">
          <ul className="sales-tasks">
            {tasks.data.map((task) => (
              <li key={task.orderId}>
                <button type="button" className="notice-link w-num" onClick={() => open(task.orderId)}>
                  {task.number}
                </button>{" "}
                {t("sales.taskDue", { date: formatDate(task.due) })}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {list.error ? <Notice tone="danger" title={errorNotice(list.error).title} className="stock-notice" /> : null}
      <ListTable
        label={t("sales.title")}
        columns={columns}
        rows={list.data ?? []}
        rowKey={(o) => o.id}
        empty={list.data ? t("sales.empty") : undefined}
        onRowClick={(o) => open(o.id)}
      />
    </div>
  );
}

const inDays = (days: number) => new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);

/** Новый заказ: покупатель из контрагентов и дата отгрузки, строки на карточке. */
function NewOrder({ onCreated }: { onCreated: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const customers = useQuery({ ...customersQuery, enabled: open });
  const [customer, setCustomer] = useState("");
  const [date, setDate] = useState(() => inDays(7));
  const [notice, setNotice] = useState<NoticeState | null>(null);
  // Пока руками не выбран, покупатель первый в списке: select его и показывает.
  const chosen = customer || customers.data?.[0]?.id || "";
  const create = useMutation({ mutationFn: () => salesApi.create(chosen, date) });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    create.mutate(undefined, {
      onSuccess: (o) => {
        void queryClient.invalidateQueries({ queryKey: SALES });
        setOpen(false);
        onCreated(o.id);
      },
      onError: (err) => setNotice(errorNotice(err)),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      trigger={<Button variant="create">{t("sales.new")}</Button>}
      title={t("sales.new")}
      footer={
        <Button type="submit" form="sales-new" variant="primary" disabled={create.isPending || !chosen}>
          {t("sales.create")}
        </Button>
      }
    >
      <form id="sales-new" className="stock-form" onSubmit={submit}>
        {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
        {customers.data && customers.data.length === 0 ? <Notice tone="warning" title={t("sales.noCustomers")} /> : null}
        <SelectField
          label={t("sales.customer")}
          value={chosen}
          onChange={(e) => setCustomer(e.target.value)}
          options={(customers.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <TextField label={t("sales.shipDate")} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </form>
    </Dialog>
  );
}
